import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Price from '../components/Price';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { convert, formatMoney } from '../utils/fx';
import { setPostAuthIntent, clearPostAuthIntent } from '../utils/postAuth';
import './Pricing.css';

// Canonical annual prices in USD (la divisa ancla del proveedor de FX).
// Se muestran convertidos a la divisa elegida; el monto en USD es el real.
// El equivalente mensual se deriva (annual / 12).
const BASE_CURRENCY = 'USD';

// Ventas (plan Seller) se atiende por WhatsApp: +52 55 4792 7091 (México).
// wa.me exige el número en formato internacional, solo dígitos.
const SALES_WHATSAPP = '525547927091';
const PLANS = [
  {
    key: 'pro',
    price: null,
    free: true, // Troly Pro es gratis para siempre
    cta: 'start',
    ctaTo: '/waitlist',
    ctaStyle: 'primary',
    featured: false,
  },
  {
    key: 'premium',
    price: 49,
    cta: 'start',
    ctaTo: '/waitlist',
    ctaStyle: 'primary',
    featured: true,
  },
  {
    key: 'seller',
    price: null, // custom pricing
    cta: 'sales',
    ctaTo: '/contacto',
    ctaStyle: 'ghost',
    featured: false,
  },
];

// Plan que corresponde a cada rol para marcarlo como "Tu plan actual".
const ROLE_TO_PLAN = { user: 'pro', user_premium: 'premium', seller: 'seller' };

export default function Pricing() {
  const { t, i18n } = useTranslation();
  const { user, createPremiumCheckout, openBillingPortal } = useAuth();
  const { rates, displayCurrency } = useCurrency();
  const gridRef = useRef(null);
  const currentPlanKey = ROLE_TO_PLAN[user?.role] || null;
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  // Premium con sesión iniciada → Stripe Checkout (redirect a la URL hosted).
  // Si ya es premium → portal de facturación para gestionar la suscripción.
  const handlePremiumCta = async () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const { url } = user.role === 'user_premium'
        ? await openBillingPortal()
        : await createPremiumCheckout();
      window.location.assign(url);
    } catch (err) {
      setCheckoutError(err.response?.data?.message || t('pricing.checkout_error'));
      setCheckoutLoading(false);
    }
  };

  // Equivalente mensual convertido a la divisa elegida (referencial, como en
  // los listings). Sin tasas aún → se muestra en USD, igual que <Price>.
  const perMonth = (annualUSD) => {
    const to = rates ? (displayCurrency || BASE_CURRENCY) : BASE_CURRENCY;
    const amount = convert(annualUSD / 12, BASE_CURRENCY, to, rates);
    return formatMoney(amount, to, i18n?.language);
  };

  // Subtle entrance: fade + translate-y as each card scrolls into view.
  useEffect(() => {
    const cards = gridRef.current?.querySelectorAll('.pricing-card');
    if (!cards?.length) return;

    if (!('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      cards.forEach((c) => c.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('pricing.meta_title')}</title>
        <meta name="description" content={t('pricing.meta_desc')} />
        <link rel="canonical" href="https://troly.club/precios" />
      </Helmet>

      <main className="pricing">
        <header className="pricing__head">
          <span className="pricing__eyebrow">{t('pricing.eyebrow')}</span>
          <h1 className="pricing__title">{t('pricing.title')}</h1>
          <p className="pricing__subtitle">{t('pricing.subtitle')}</p>
        </header>

        <section className="pricing__grid" ref={gridRef}>
          {PLANS.map((plan, i) => {
            const features = t(`pricing.plans.${plan.key}.features`, {
              returnObjects: true,
            });
            const featureList = Array.isArray(features) ? features : [];
            const isSales = plan.key === 'seller';

            return (
              <article
                key={plan.key}
                className={`pricing-card${plan.featured ? ' pricing-card--featured' : ''}`}
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                {plan.featured && (
                  <span className="pricing-card__badge">{t('pricing.badge_popular')}</span>
                )}
                {plan.key === currentPlanKey && (
                  <span className="pricing-card__badge pricing-card__badge--current">
                    {t('pricing.badge_current')}
                  </span>
                )}

                <h2 className="pricing-card__name">{t(`pricing.plans.${plan.key}.name`)}</h2>
                <p className="pricing-card__tagline">{t(`pricing.plans.${plan.key}.tagline`)}</p>

                {plan.free ? (
                  <div className="pricing-card__price">
                    <span className="pricing-card__free">{t('pricing.free')}</span>
                    <span className="pricing-card__free-forever">{t('pricing.free_forever')}</span>
                  </div>
                ) : plan.price != null ? (
                  <div className="pricing-card__price">
                    <Price className="pricing-card__amount" amount={plan.price} currency={BASE_CURRENCY} />
                    <span className="pricing-card__period">{t('pricing.per_year')}</span>
                    <span className="pricing-card__permonth">
                      {t('pricing.per_month_equiv', { price: perMonth(plan.price) })}
                    </span>
                  </div>
                ) : (
                  <div className="pricing-card__price pricing-card__price--custom">
                    <span className="pricing-card__custom">{t('pricing.custom_price')}</span>
                    <p className="pricing-card__desc">{t('pricing.plans.seller.desc')}</p>
                  </div>
                )}

                <ul className="pricing-card__features">
                  {featureList.map((f, idx) => (
                    <li key={idx} className="pricing-card__feature">
                      <svg className="pricing-card__check" width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M4 10.5l3.5 3.5L16 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="pricing-card__foot">
                  {isSales ? (
                    <a
                      className={`pricing-card__cta pricing-card__cta--${plan.ctaStyle}`}
                      href={`https://wa.me/${SALES_WHATSAPP}?text=${encodeURIComponent(t('pricing.wa_sales_message'))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('pricing.cta_sales')}
                    </a>
                  ) : plan.key === 'premium' && user ? (
                    <button
                      type="button"
                      className={`pricing-card__cta pricing-card__cta--${plan.ctaStyle}`}
                      onClick={handlePremiumCta}
                      disabled={checkoutLoading}
                    >
                      {checkoutLoading
                        ? t('pricing.cta_loading')
                        : user.role === 'user_premium'
                          ? t('pricing.cta_manage')
                          : t('pricing.cta_start')}
                    </button>
                  ) : (
                    <Link
                      className={`pricing-card__cta pricing-card__cta--${plan.ctaStyle}`}
                      to={plan.ctaTo}
                      onClick={plan.key === 'premium'
                        // Sin sesión: se recuerda la intención para que, tras
                        // registrarse y llegar a /profile, se lance el Stripe
                        // Checkout de Premium automáticamente. Elegir Pro
                        // (gratis) descarta una intención Premium previa.
                        ? () => setPostAuthIntent({ type: 'premium_checkout' })
                        : () => clearPostAuthIntent()}
                    >
                      {t('pricing.cta_start')}
                    </Link>
                  )}
                  {plan.key === 'premium' && checkoutError && (
                    <span className="pricing-card__note pricing-card__note--error" role="alert">
                      {checkoutError}
                    </span>
                  )}
                  {plan.cta === 'start' && (
                    <span className="pricing-card__note">{t(`pricing.plans.${plan.key}.note`)}</span>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      </main>
    </>
  );
}
